import { Comment, IComment } from '@app/models/Comment'
import { ApplicationState } from '@app/store'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { EditorState } from 'draft-js'
import { Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ICommentEditorState {
  readonly comments: Map<string, IComment>
}

const StateRecord = Record({
  comments: Map<string, Comment>(),
})

export class CommentEditorState extends StateRecord
  implements ICommentEditorState {
  public static fromJS(initialState?: Partial<ICommentEditorState>) {
    return new CommentEditorState(initialState)
  }

  public readonly comments: Map<string, Comment>

  constructor(props?: Partial<ICommentEditorState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum CommentEditorActionTypes {
  Init = 'COMMENT_EDITOR::INIT',
  SaveComment = 'COMMENT_EDITOR::SAVE_COMMENT',
  SaveCommentFailure = 'COMMENT_EDITOR::SAVE_COMMENT_FAILURE',
  SaveCommentRequest = 'COMMENT_EDITOR::SAVE_COMMENT_REQUEST',
  SaveCommentSuccess = 'COMMENT_EDITOR::SAVE_COMMENT_SUCCESS',
  SetIsPublic = 'COMMENT_EDITOR::SET_IS_PUBLIC',
  SetMarkup = 'COMMENT_EDITOR::SET_MARKUP',
}

interface IInitAction {
  defaultPublic?: boolean
  entityId: string
  type: CommentEditorActionTypes.Init
}

interface ISaveCommentAction {
  comment: IComment
  entityId: string
  type: CommentEditorActionTypes.SaveComment
}

interface ISaveCommentFailureAction {
  entityId: string
  type: CommentEditorActionTypes.SaveCommentFailure
}

interface ISaveCommentRequestAction {
  entityId: string
  type: CommentEditorActionTypes.SaveCommentRequest
}

export interface ISaveCommentSuccessAction {
  comment: Comment
  entityId: string
  type: CommentEditorActionTypes.SaveCommentSuccess
}

interface ISetIsPublicAction {
  entityId: string
  isPublic: boolean
  type: CommentEditorActionTypes.SetIsPublic
}

interface ISetMarkupAction {
  entityId: string
  markupState: EditorState
  type: CommentEditorActionTypes.SetMarkup
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IInitAction
  | ISaveCommentFailureAction
  | ISaveCommentRequestAction
  | ISaveCommentSuccessAction
  | ISetIsPublicAction
  | ISetMarkupAction

// -------------------------
// ACTION CREATORS
// -------------------------

function init(entityId: string, defaultPublic?: boolean): IInitAction {
  return {
    defaultPublic,
    entityId,
    type: CommentEditorActionTypes.Init,
  }
}

function saveComment(entityId: string, comment: IComment): ISaveCommentAction {
  return {
    comment,
    entityId,
    type: CommentEditorActionTypes.SaveComment,
  }
}

function saveCommentFailure(entityId: string): ISaveCommentFailureAction {
  return {
    entityId,
    type: CommentEditorActionTypes.SaveCommentFailure,
  }
}

function saveCommentRequest(entityId: string): ISaveCommentRequestAction {
  return {
    entityId,
    type: CommentEditorActionTypes.SaveCommentRequest,
  }
}

function saveCommentSuccess(
  entityId: string,
  comment: Comment,
): ISaveCommentSuccessAction {
  return {
    comment,
    entityId,
    type: CommentEditorActionTypes.SaveCommentSuccess,
  }
}

function setIsPublic(entityId: string, isPublic: boolean): ISetIsPublicAction {
  return {
    entityId,
    isPublic,
    type: CommentEditorActionTypes.SetIsPublic,
  }
}

function setMarkup(
  entityId: string,
  markupState: EditorState,
): ISetMarkupAction {
  return {
    entityId,
    markupState,
    type: CommentEditorActionTypes.SetMarkup,
  }
}

export const actionCreators = {
  init,
  saveComment,
  setIsPublic,
  setMarkup,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<CommentEditorState> = (
  state: CommentEditorState = new CommentEditorState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case CommentEditorActionTypes.Init:
      if (!state.hasIn(['comments', action.entityId])) {
        return state.setIn(
          ['comments', action.entityId],
          new Comment({
            defaultPublic: action.defaultPublic || false,
            isPublic: action.defaultPublic || false,
            parentId: action.entityId,
          }),
        ) as CommentEditorState
      } else {
        return state
      }

    case CommentEditorActionTypes.SaveCommentFailure:
      if (state.hasIn(['comments', action.entityId])) {
        return state.setIn(
          ['comments', action.entityId, 'isSaving'],
          false,
        ) as CommentEditorState
      } else {
        return state
      }

    case CommentEditorActionTypes.SaveCommentRequest:
      if (state.hasIn(['comments', action.entityId])) {
        return state.setIn(
          ['comments', action.entityId, 'isSaving'],
          true,
        ) as CommentEditorState
      } else {
        return state
      }

    case CommentEditorActionTypes.SaveCommentSuccess:
      if (state.hasIn(['comments', action.entityId])) {
        const comment: Comment = state.getIn(['comments', action.entityId])
        return state
          .setIn(
            ['comments', action.entityId, 'isPublic'],
            comment.defaultPublic,
          )
          .setIn(
            ['comments', action.entityId, 'markupState'],
            EditorState.createEmpty(),
          ) as CommentEditorState
      } else {
        return state
      }

    case CommentEditorActionTypes.SetIsPublic:
      return state.setIn(
        ['comments', action.entityId, 'isPublic'],
        action.isPublic,
      ) as CommentEditorState

    case CommentEditorActionTypes.SetMarkup:
      return state.setIn(
        ['comments', action.entityId, 'markupState'],
        action.markupState,
      ) as CommentEditorState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* saveCommentSaga(action: ISaveCommentAction) {
  try {
    yield put(saveCommentRequest(action.entityId))

    const url = `/api/FeedItems/${action.entityId}/Comments`
    const opts: IRequestOptions = {
      body: JSON.stringify({
        isPublic: action.comment.isPublic,
        markup: convertToMarkup(action.comment.markupState.getCurrentContent()),
        parentId: action.entityId,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(
        saveCommentSuccess(action.entityId, Comment.fromJS(result.entity)),
      )
      yield put(successToast('Success', 'Comment created'))
    } else {
      yield put(saveCommentFailure(action.entityId))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveCommentFailure(action.entityId))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchSaveComment() {
  yield takeEvery(CommentEditorActionTypes.SaveComment, saveCommentSaga)
}

export function* commentEditorRootSaga() {
  yield fork(watchSaveComment)
}

// -------------------------
// REDUCER
// -------------------------
const selectModuleState = (state: ApplicationState) => state.commentEditor

export const selectComment = (entityId: string) =>
  createSelector(
    selectModuleState,
    (commentEditorState) => commentEditorState.comments.get(entityId),
  )
